import { ChangeEvent, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { isAxiosError } from 'axios';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import {
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@rossum/ui/material';
import TextFieldControl from '../../../components/ReactHookForm/controls/TextFieldControl';
import { mdhUrl } from '../../../constants/config';
import { link } from '../../../lib/formaterValues';
import { notify } from '../../toaster';
import { getErrorMessage } from '../getErrorMessage';
import { DatasetsHeader } from '../header';
import { useCreateDataset } from '../hooks/useCreateDataset';
import { useDatasetUpload } from '../hooks/useDatasetUpload';
import { datasetTablePath } from '../routes';
import { pageSpacingStyles } from '../styles';
import { Columns } from './Columns';
import { CreateForm, createFormSchema } from './createFormSchema';
import { generateDatasetName } from './generateDatasetName';
import { UploadArea } from './UploadArea';

const maxFileSize = 52428800; // 50 MB
const FORM_ID = 'dataset-import';

// TODO: Cleanup the whole file
export const DatasetImport = () => {
  const intl = useIntl();
  const [file, setFile] = useState<File | null>(null);

  const { control, handleSubmit, getValues, setValue, setError, trigger } =
    useForm<CreateForm>({
      defaultValues: {
        name: '',
        metadata: {
          name: '',
          description: '',
        },
        // Do we actually need encoding in 2025?
        encoding: 'utf-8',
        columns: [],
      },
      resolver: zodResolver(createFormSchema(intl)),
    });
  const columns = useWatch({ control, name: 'columns' });
  // A dataset needs its columns from somewhere: declared here or taken from
  // the file.
  const canSubmit = columns.length > 0 || file !== null;

  const { push } = useHistory();

  const { mutate: createDataset, isLoading: isCreating } = useCreateDataset();
  const { mutate: uploadData, isLoading: isUploading } = useDatasetUpload();
  const isSaving = isCreating || isUploading;

  const onSubmit: SubmitHandler<CreateForm> = form => {
    createDataset(
      {
        name: form.name,
        metadata: form.metadata ?? {},
        columns: form.columns,
      },
      {
        onError: error => {
          if (isAxiosError(error) && error.response?.status === 409) {
            setError('name', {
              message: intl.formatMessage({
                id: 'features.datasets.create.error.idTaken',
              }),
            });
            return;
          }
          notify.error({
            title:
              getErrorMessage(error) ??
              intl.formatMessage({
                id: 'features.datasets.notification.createFailed',
              }),
          });
        },
        onSuccess: table => {
          if (!file) {
            push(datasetTablePath(table.name));
            return;
          }
          // The dataset exists once created, so a failed upload does not fail
          // the create; its Data tab can take the upload again.
          uploadData(
            {
              datasetName: table.name,
              file,
              encoding: form.encoding,
              dynamic: true,
            },
            {
              onSuccess: () => {
                notify.default({
                  title: intl.formatMessage({
                    id: 'features.datasets.notification.importStarted',
                  }),
                });
              },
              onError: error => {
                notify.error({
                  title:
                    getErrorMessage(error) ??
                    intl.formatMessage({
                      id: 'features.datasets.notification.importFailed',
                    }),
                });
              },
              onSettled: () => push(datasetTablePath(table.name)),
            }
          );
        },
      }
    );
  };

  return (
    <PageLayout
      renderHeader={props => (
        <DatasetsHeader
          {...props}
          datasetId={undefined}
          datasetTab={undefined}
          title={intl.formatMessage({
            id: 'features.datasets.uploadDataset.title',
          })}
          description={intl.formatMessage({
            id: 'features.datasets.uploadDataset.description',
          })}
          breadcrumbs={[
            {
              label: intl.formatMessage({
                id: 'features.datasets.createNew.title',
              }),
            },
          ]}
          buttons={[
            <Button
              key="save-dataset-button"
              variant="contained"
              type="submit"
              form={FORM_ID}
              disabled={isSaving || !canSubmit}
              endIcon={isSaving ? <CircularProgress size={14} /> : null}
            >
              {intl.formatMessage({
                id: 'features.datasets.settings.header.button.save',
              })}
            </Button>,
          ]}
        />
      )}
    >
      <Stack
        component="form"
        sx={pageSpacingStyles}
        gap={2}
        onSubmit={handleSubmit(onSubmit)}
        id={FORM_ID}
      >
        <ContentGroup
          title={intl.formatMessage({
            id: 'features.datasets.settings.form.idSection.title',
          })}
          description={
            <Stack spacing={1}>
              <Typography variant="inherit">
                {intl.formatMessage({
                  id: 'features.datasets.settings.form.idSection.description',
                })}
              </Typography>
              {/* TODO: Link */}
            </Stack>
          }
        >
          <Stack spacing={2}>
            <TextFieldControl
              ControllerProps={{ control, name: 'metadata.name' }}
              label={intl.formatMessage({
                id: 'features.datasets.settings.form.idSection.name.label',
              })}
              FieldLabelProps={{
                layout: 'floating',
              }}
              onBlur={() => {
                if (getValues('name') === '') {
                  setValue(
                    'name',
                    generateDatasetName(getValues('metadata.name') ?? '')
                  );
                  trigger('name');
                }
              }}
            />

            <TextFieldControl
              ControllerProps={{ control, name: 'name' }}
              label={intl.formatMessage({
                id: 'features.datasets.settings.form.idSection.id.label',
              })}
              helperText={intl.formatMessage({
                id: 'features.datasets.settings.form.idSection.id.helperText',
              })}
              FieldLabelProps={{
                layout: 'floating',
              }}
            />

            <TextFieldControl
              ControllerProps={{ control, name: 'metadata.description' }}
              label={intl.formatMessage({
                id: 'features.datasets.settings.form.idSection.description.label',
              })}
              FieldLabelProps={{
                layout: 'floating',
              }}
              multiline
              maxRows={3}
            />
          </Stack>
        </ContentGroup>
        <Columns control={control} setValue={setValue} />
        <ContentGroup
          title={intl.formatMessage({
            id: 'features.datasets.uploadFile.title',
          })}
          description={
            <Stack spacing={1}>
              <Typography variant="inherit">
                {intl.formatMessage({
                  id: 'features.datasets.uploadFile.description',
                })}
              </Typography>
              <Typography variant="inherit">
                {intl.formatMessage(
                  { id: 'features.datasets.uploadFile.docs' },
                  {
                    link: link(
                      `${mdhUrl}/svc/master-data-hub/api/docs#tag/Dataset`
                    ),
                  }
                )}
              </Typography>
            </Stack>
          }
        >
          <UploadArea
            file={file}
            onUploadChange={(e: ChangeEvent<HTMLInputElement>) => {
              const selectedFile = e.target.files?.[0];
              if (selectedFile) {
                if (selectedFile.size > maxFileSize) {
                  // return setFileSizeError(true);
                }
                // setFileSizeError(false);
                return setFile(selectedFile);
              }
              return setFile(null);
            }}
          />
        </ContentGroup>
      </Stack>
    </PageLayout>
  );
};
